import axios from "axios"
import React, { useEffect, useState } from "react"

import { Button, Dialog, DialogHeader, DialogBody, DialogFooter, Tooltip } from "@material-tailwind/react"
import { CheckIcon, Cog8ToothIcon, FunnelIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/solid"
import DataTableShell from "../../../../Components/table/DataTableShell"
import TableToolbar from "../../../../Components/table/TableToolbar"
import TableSearch from "../../../../Components/table/TableSearch"
import {
    TableActionGroup,
    TableEmptyRow,
    TableIdCell,
    TableSkeletonRows,
} from "../../../../Components/table/TableCells"
import useClientTable from "../../../../Components/table/useClientTable"

const TABLE_HEAD = ["Brand", "SKU Initial", "Title", "Profile ID", "Actions"]
const matchesFilter = (item, query) =>
    !query ||
    [item.brandName, item.brandTitle, item.skuInitial, item.profileId]
        .some(value => String(value ?? '').toLowerCase().includes(query))
const sortByBrand = (a, b) => String(a.brandName).localeCompare(String(b.brandName))

export function FilterViews({ isAltereds }) {
    const [open, setOpen] = useState(false)
    const [editingBrand, setEditingBrand] = useState(null)
    const [isAltered, setIsAltered] = useState(false)
    const [brandFilters, setBrandFilters] = useState([])
    const [editFilter, setEditFilter] = useState({ skuInitial: '', brandTitle: '', profileId: ''})
    const [isLoading, setIsLoading] = useState(true)
    const {
        searchTerm,
        handleSearch,
        currentPage,
        setCurrentPage,
        pageItems,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: brandFilters,
        pageSize: 9,
        filterFn: matchesFilter,
        sortFn: sortByBrand,
    })

    useEffect (() => {
        axios.get('/api/brand-filters/all-filters')
        .then(response => {
            if (response.data.message) {
                console.log("Cannot get batches")
            } else {
                const filters = response.data.result.flatMap(item => item.filters)
                setBrandFilters(filters)
                console.log(filters)
            } 
        })
        .catch(error => {
            console.error('Error:', error)
        })
        .finally(() => setIsLoading(false))
    },[isAltered, isAltereds])

    const handleOpen = () => setOpen(!open)

    const handleEditMode = (item) => {
        if (editingBrand === item.brandName) {
            setEditingBrand(null)
            setEditFilter({ skuInitial: '', brandTitle: '', profileId: '' })
            return
        }
        setEditingBrand(item.brandName)
        setEditFilter({
            skuInitial: item.skuInitial || '',
            brandTitle: item.brandTitle || '',
            profileId: item.profileId || '',
        })
    }

    const handleSaveEdits = (item) => {
        const updatedFilter = {
            brandName: item.brandName,
            skuInitial: editFilter.skuInitial,
            brandTitle: editFilter.brandTitle,
            profileId: editFilter.profileId
        };
    
        axios.put(`/api/brand-filters/update-filter/${item.brandName}`, updatedFilter)
            .then(response => {
                if (response.data.message) {
                    setEditingBrand(null);
                    setIsAltered(prevState => !prevState);
                    setEditFilter({ skuInitial: '', brandTitle: '', profileId: '' });
                    window.alert(`${response.data.message}!`);
                } else {
                    window.alert('Unknown error occurred');
                }
            })
            .catch(error => {
                console.error('Error:', error);
                const errorMessage = error.response?.data?.message || 'An error occurred during update.';
                window.alert(errorMessage);
            });
    };

    const handleDeleteFilters = (brandName) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete this brand filter: ${brandName}?`)

        if (isConfirmed) {
            axios.delete(`/api/brand-filters/delete-filter/${brandName}`)
                .then(response => {
                    if (response.data.message) {
                        window.alert(`${response.data.message}!`)
                        setIsAltered(prevState => !prevState)
                    } else {
                        window.alert(`${response.data.message}`)
                        console.log('Error:', response.data.error)
                    }
                })
                .catch(error => {
                    console.error('Error:', error)
                })
        }
    }

    return (
        <>
        <Tooltip content="View Filters">
            <FunnelIcon onClick={handleOpen} className="bg-white bg-opacity-15 hover:bg-opacity-30 rounded-lg p-1 h-10 w-10 text-cyan-300 cursor-pointer hover:scale-110 transition mr-1 group-hover:animate-spin "/>
        </Tooltip>

        <Dialog open={open} handler={handleOpen} className="bg-surface" size="lg">
            <DialogHeader className="text-white"><Cog8ToothIcon className="h-7 w-7 mr-1"/>Brand Filter Settings</DialogHeader>
            <DialogBody className="flex flex-col">
                <DataTableShell
                    minWidth="760px"
                    toolbar={
                        <TableToolbar
                            kicker="Data gathering"
                            title="Brand Filters"
                            meta={`${totalFiltered} of ${brandFilters.length} filters`}
                        >
                            <TableSearch
                                id="brand-filter-search"
                                value={searchTerm}
                                onChange={handleSearch}
                                placeholder="Search filters"
                            />
                        </TableToolbar>
                    }
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                >
                    <thead>
                        <tr>
                            {TABLE_HEAD.map(head => <th key={head}>{head}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableSkeletonRows columns={TABLE_HEAD.length} rows={9} />
                        ) : pageItems.length === 0 ? (
                            <TableEmptyRow
                                columns={TABLE_HEAD.length}
                                title="No brand filters found"
                                description="Try a different brand, title, SKU initial, or profile ID."
                            />
                        ) : pageItems.map(item => {
                            const isEditing = editingBrand === item.brandName
                            return (
                            <tr key={item.brandName}>
                            <td><TableIdCell>{item.brandName}</TableIdCell></td>
                            {isEditing ?
                                <td>
                                    <input
                                    className="nx-input min-w-[9rem]"
                                    value={editFilter.skuInitial}
                                    type="text"
                                    onChange={e => setEditFilter({ ...editFilter, skuInitial: e.target.value })}
                                    /> 
                                </td>
                            :
                                <td>{item.skuInitial || '-----'}</td>
                            }
                            {isEditing ?
                                <td>
                                    <input
                                    className="nx-input min-w-[9rem]"
                                    value={editFilter.brandTitle}
                                    type="text"
                                    onChange={e => setEditFilter({ ...editFilter, brandTitle: e.target.value })}
                                    />  
                                </td>
                            :
                                <td>{item.brandTitle || '-----'}</td>
                            }
                            {isEditing ?
                                <td>
                                    <input
                                    className="nx-input min-w-[9rem]"
                                    value={editFilter.profileId}
                                    type="text"
                                    onChange={e => setEditFilter({ ...editFilter, profileId: e.target.value })}
                                    />                                    
                                </td>
                            :
                                <td><TableIdCell>{item.profileId || '-----'}</TableIdCell></td>
                            }

                            <td>
                                {isEditing ? 
                                <TableActionGroup>
                                    <button
                                        type="button"
                                        className="nx-btn-primary !px-2"
                                        onClick={() => handleSaveEdits(item)}
                                        aria-label={`Save edits for ${item.brandName}`}
                                    >
                                        <CheckIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        className="nx-btn-ghost !px-2"
                                        onClick={() => handleEditMode(item)}
                                        aria-label={`Cancel edits for ${item.brandName}`}
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </TableActionGroup>
                                :
                                <TableActionGroup>
                                    <button
                                        type="button"
                                        className="nx-btn-ghost !px-2"
                                        onClick={() => handleEditMode(item)}
                                        aria-label={`Edit ${item.brandName}`}
                                    >
                                        <PencilSquareIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        className="nx-btn-ghost !px-2 text-red-400"
                                        onClick={() => handleDeleteFilters(item.brandName)}
                                        aria-label={`Delete ${item.brandName}`}
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </TableActionGroup>
                                }
                            </td>
                            </tr>
                        )
                        })}
                    </tbody>
                </DataTableShell>
            </DialogBody>
            <DialogFooter>
                <Button
                    variant="text"
                    color="red"
                    onClick={handleOpen}
                    className="mr-1"
                >
                    <span>Cancel</span>
                </Button>
                <Button variant="gradient" color="green">Save</Button>
            </DialogFooter>
        </Dialog>
    </>
    )
}
